/**
 * 매뉴얼 이미지 자산 로더.
 *
 * 본문(`docs/manual/**\/*.md`)이 `![캡션](./images/x.png)`처럼 **문서 기준 상대 경로**로 그림을
 * 참조하는데, 번들된 화면에서는 그 경로가 아무 의미가 없다(그대로 내보내면 404). 그래서 마크다운과
 * 같은 방식으로 이미지도 빌드 타임에 수집해, **레포 상대 경로 → 번들 URL** 표를 만들어 둔다.
 *
 * 해석(`resolveManualAsset`)은 파일 시스템을 모르는 순수 함수라 단위 테스트가 실제 자산에
 * 의존하지 않는다 — 수집(`import.meta.glob`)만 이 모듈이 담당한다.
 */

/** 그대로 통과시키는 외부 참조 (레포 자산이 아니다). */
const EXTERNAL_RE = /^(https?:)?\/\/|^data:|^blob:/i;

// `apps/admin/src/pages/manual/` → 레포 루트까지 5단계 (`manualContent.ts`와 같은 규약).
const rawAssets = import.meta.glob('../../../../../docs/manual/**/*.{png,jpg,jpeg,webp,svg,gif}', {
  query: '?url',
  import: 'default',
  eager: true,
}) as Record<string, string>;

/**
 * glob 키를 레포 상대 경로로 정규화한다.
 * `../../../../../docs/manual/composer/how-to/images/x.png` → `docs/manual/composer/how-to/images/x.png`
 */
export function normalizeAssetKey(globKey: string): string {
  const normalized = globKey.replace(/\\/g, '/');
  const at = normalized.lastIndexOf('docs/manual/');
  return at < 0 ? normalized.replace(/^\.\//, '') : normalized.slice(at);
}

/** `.`/`..`를 접어 경로를 평탄화한다 (선행 `/`는 레포 루트 기준으로 본다). */
function normalizePath(path: string): string {
  const out: string[] = [];
  for (const part of path.split('/')) {
    if (!part || part === '.') continue;
    if (part === '..') {
      out.pop();
      continue;
    }
    out.push(part);
  }
  return out.join('/');
}

/** 수집 결과를 **레포 상대 경로 → URL** 표로 만든다. */
export function buildAssetMap(files: Record<string, string>): Map<string, string> {
  return new Map(Object.entries(files).map(([key, url]) => [normalizeAssetKey(key), url]));
}

/**
 * 문서 경로(`docs/manual/<매뉴얼>/<섹션>/<slug>.md`)가 놓인 디렉토리(`docs/manual/<매뉴얼>/<섹션>`).
 *
 * ⚠️ 예시에 **실재하지 않는 리터럴 경로를 쓰지 않는다** — 종전에는 manual 하위를 한 글자 세그먼트
 * 셋으로 적었고, 그 줄이 `ws:index-check` 갈래 11(소스 주석이 가리킨 문서가 실재하나)의 needle에
 * 걸려 **검사가 이 JSDoc을 「부재」로 보고**했다(레포 유일의 상시 경고였다). `<…>` 형태는 그 needle의
 * 문자 집합에 없어 매칭이 성립하지 않고, `scripts/manual/drift.mjs`가 같은 경로 규약을 이미 그렇게 쓴다.
 *
 * ⚠️ **이 경고를 «설명하는» 문장에도 같은 제약이 걸린다 — 여기에 그 꼴의 리터럴을 인용하지 말 것.**
 * 2026-10-13에 위 문단을 쓰면서 종전 예시를 그대로 인용했다가 **경고가 1건 그대로 남았다**. 고친
 * 코드를 읽어서는 드러나지 않았고 `pnpm -s ws:index-check` **출력을 다시 재서야** 보였다 — 「고쳤다」는
 * 출력으로 재야 판정이 된다. 경로를 말해야 하면 `<…>`나 `*`를 섞어 needle 밖으로 낼 것.
 *
 * 경로가 없으면 빈 문자열 — **던지지 않는다.** 이미지가 안 붙는 것과 문서가 통째로 안 열리는 것은
 * 전혀 다른 사고다(`manualStale.ts`와 같은 원칙). 경로가 비면 `resolveManualAsset`이 `null`을
 * 돌려주고 호출부가 "이미지를 찾을 수 없습니다"를 렌더한다.
 */
export function manualDocDir(docPath: string | undefined): string {
  if (!docPath) return '';
  const normalized = normalizeAssetKey(docPath);
  const at = normalized.lastIndexOf('/');
  return at < 0 ? '' : normalized.slice(0, at);
}

/** 빌드 타임에 수집된 매뉴얼 이미지 표. */
export const manualAssets: Map<string, string> = buildAssetMap(rawAssets);

/**
 * 본문의 이미지 `src`를 번들 URL로 해석한다.
 *
 * - 외부 URL(`https://`, `data:` 등)은 그대로 통과
 * - 그 외는 `docDir` 기준 상대 경로로 보고 정규화해 표에서 찾는다
 * - **없으면 `null`** — 빈 문자열이나 원본을 돌려주면 호출부가 실패를 구분할 수 없어,
 *   깨진 `<img>`가 조용히 화면에 남는다
 */
export function resolveManualAsset(
  docDir: string,
  src: string,
  assets: Map<string, string> = manualAssets,
): string | null {
  const target = src.trim();
  if (!target) return null;
  if (EXTERNAL_RE.test(target)) return target;

  // 쿼리·해시는 조회 키에서 뺀다(`x.png?v=2` 같은 표기를 허용).
  const clean = target.replace(/[?#].*$/, '');
  const joined = clean.startsWith('/') ? clean.slice(1) : `${docDir}/${clean}`;
  return assets.get(normalizePath(joined)) ?? null;
}
